import { ApiResponse, PaginatedCollection, directusApi } from '@/api';
import { asyncForEach, publicationStoreBadge } from '@/utils';
import lodash, { map } from 'lodash';
import moment from 'moment-timezone';
import * as XLSX from 'xlsx';

export interface LinkedProduct {
    // sku: string;
    qty: string;
    discount: string;
    id: string;
    title: string;
    post_type: string;
    stock: string;
    price: string;
    warehouses: Array<any>;
    name?: string;
}

export interface Publication {
    id: string;
    title: string;
    autosync: boolean;
    price_sync: boolean;
    stock_sync: boolean;
    own_post: boolean;
    listing_type: string;
    logistic_type: string;
    linked_products_string: string;
    linked_products: Array<LinkedProduct | any>;
    shipment_cost: number;
    shipment_constant: number;
    default_shipment_cost: number;
    iva: number;
    category_id: string;
    last_sync_up: string;
    last_sync_down: string;
    last_update: string;
    variations: Array<any>;
    category: any;
    categoryCommission: any;
    sync_warehouses: any;
    categoryAttributes: any;
    category_variations: Array<any>;
    cat_variation: Array<any>;
    officialStore: any;
    categoryPredictor: any;
    channels: any;
}

export interface PublicationDTO {
    title: string;
}

export interface PublicationState {
    activePublication: Publication | null;
    activePublicationLoading: boolean;

    allPublications: Array<Publication>;
    allPublicationsLoading: boolean;
    publicationsPerPage: number;
    publicationsPage: number;
    publicationsTotal: number;
    categoryPredictor: Array<any>;
    linkedProductsSummary: boolean;
    by_stock_loading: boolean;
    post_loading: boolean;
}

const defaultPublication = {
    id: '',
    title: '',
    autosync: false,
    price_sync: false,
    stock_sync: false,
    own_post: true,
    listing_type: 'gold_special',
    logistic_type: '',
    linked_products_string: '[]',
    linked_products: [{}],
    // linked_products: [
    //     {
    //         // sku: '0',
    //         qty: '0',
    //         discount: '0',
    //         id: '0',
    //         title: '0',
    //         post_type: '0',
    //         stock: '0',
    //         price: '0',
    //         warehouses: []
    //     },
    // ],
    shipment_cost: 25,
    shipment_constant: 298.5,
    default_shipment_cost: 25,
    iva: 0.16,
    category_id: '',
    last_sync_up: '',
    last_sync_down: '',
    last_update: '',
    variations: [],
    category: {},
    categoryCommission: '',
    sync_warehouses: [],
    categoryAttributes: [],
    category_variations: [],
    cat_variation: [],
    gallery: [
        {
            "id": "013324ba-35b4-4e1c-9bf1-055d051bd5e8",
            "origin": "directus",
            "url": "https://directus.prodelec.app/assets/013324ba-35b4-4e1c-9bf1-055d051bd5e8",
            "sort": null
        },
        {
            "id": "adfe3613-1be4-4ad2-9f93-df2785f3498f",
            "origin": "directus",
            "url": "https://directus.prodelec.app/assets/adfe3613-1be4-4ad2-9f93-df2785f3498f",
            "sort": null
        }
    ],
    officialStore: null,
    categoryPredictor: [],
    channels: []
}

const state: PublicationState = {
    activePublication: defaultPublication,
    activePublicationLoading: false,

    allPublications: [],
    allPublicationsLoading: false,
    publicationsPerPage: 1,
    publicationsPage: 1,
    publicationsTotal: 1,
    categoryPredictor: [],
    linkedProductsSummary: false,
    by_stock_loading: false,
    post_loading: false

};

const mutations = {
    TOGGLE_ALL_PUBLICATIONS_LOADING(state: PublicationState, data: boolean) {
        state.allPublicationsLoading = data;
    },
    SET_ALL_PUBLICATIONS(state: PublicationState, data: PaginatedCollection) {
        state.allPublications = data.items;
        state.publicationsPerPage = data.perPage;
        state.publicationsPage = data.currentPage;
        state.publicationsTotal = data.totalItems;
    },
    TOGGLE_ACTIVE_PUBLICATION_LOADING(state: PublicationState, data: boolean) {
        state.activePublicationLoading = data;
    },
    SET_ACTIVE_PUBLICATION(state: PublicationState, data: Publication) {
        if (data.id) {
            let allPublications = [data, ...state.allPublications];
            state.allPublications = lodash.uniqBy(allPublications, 'id');
            state.activePublication = data;
        } else {
            state.activePublication = data;
        }
        console.log('SET_ACTIVE_PUBLICATION ', state.activePublication);
    },
    SET_CATEGORY_ATTRIBUTES(state: PublicationState, attributes: any) {
        state.activePublication.categoryAttributes = attributes;
    },
    SET_VARIATIONS(state: PublicationState, variations: any) {
        state.activePublication.cat_variation = variations;
    },
    SET_CATEGORY_PREDICTOR(state: PublicationState, categories: any) {
        state.activePublication.categoryPredictor = categories;
    },
    SET_CATEGORY_COMMISSION(state: PublicationState, commission: any) {
        state.activePublication.categoryCommission = commission;
    },
    linkedSummaryLoading(state: PublicationState, data: boolean) {
        state.linkedProductsSummary = data;
    },
    byStockLoading(state: PublicationState, data: boolean) {
        state.by_stock_loading = data;
    },
    postLoading(state: PublicationState, data: boolean) {
    state.post_loading = data;
    },
};

const actions = {
    async putOne({ _, commit }: any, { publicationId, autosync, price_sync, stock_sync, publicationData = null, sync_warehouses, description, store, channels }: any) {
        commit('TOGGLE_ACTIVE_PUBLICATION_LOADING', true);

        if (price_sync == false && stock_sync == false) { //validation for autosync
            autosync = false;
        } else {
            autosync = true;
        }

        // console.log({ autosync });
        // console.log({ price_sync });
        // console.log({ stock_sync });

        let payload: any = {
            autosync,
            price_sync,
            stock_sync,
            sync_warehouses,
            channels
        };

        // updates mercadolibre_post variations
        if (publicationData) {
            await asyncForEach(publicationData.variations, async variation => {
                await directusApi('PATCH', `items/mercadolibre_variations/${variation.id}`, {
                    linked_products: variation.linked_products.map((linked_product: any) => {
                        return {
                            sku: linked_product.id,
                            qty: linked_product.qty,
                            discount: linked_product.discount,
                        };
                    }),
                }).catch(err => {
                    console.log(err);
                });
            });
            payload = {
                ...payload,
                linked_products: publicationData.linked_products.map((linked_product: any) => {
                    return {
                        sku: linked_product.id,
                        qty: linked_product.qty,
                        discount: linked_product.discount,
                    };
                }),
            };
        }

        // updates mercadolibre_post
        await directusApi('PATCH', `items/mercadolibre_posts/${publicationId}`, payload)
            .then(async () => {
                store = store.toLowerCase();
                let payload = { id: publicationId, description: description, store };
                // console.log({payload});

                await directusApi('POST', `custom/legacy-mercadolibre/updatePublication`, payload)
                    .then(res => {
                        console.log(res.data);
                        // commit('SET_ACTIVE_PUBLICATION', res.data);
                    })
                    .catch(err => {
                        console.log(err);
                    });

                await directusApi('POST', `custom/legacy-mercadolibre/updateDescription`, payload)
                    .then(res => {
                        console.log('ML PUT description Updated');
                    }).catch((err) => {
                        console.log(err);
                    });

                // gets complete data of publication
                await directusApi('GET', `custom/legacy-mercadolibre/publications/${publicationId}`)
                    .then(res => {
                        commit('SET_ACTIVE_PUBLICATION', res.data);
                    })
                    .catch(err => {
                        console.log(err);
                    });
            })
            .catch(err => {
                console.log(err);
            })
            .finally(() => {
                commit('TOGGLE_ACTIVE_PUBLICATION_LOADING', false);
            });
    },

    async postOne({ _, commit, dispatch }: any, { autosync, price_sync, stock_sync, sync_warehouses, publication, title, description, attributes, cost, linked_products, user, channels }: any) {
        let created_by = user.first_name + ' ' + user.last_name;
        commit('TOGGLE_ACTIVE_PUBLICATION_LOADING', true);

        if (price_sync == false && stock_sync == false) { //validation for autosync
            autosync = false;
        } else {
            autosync = true;
        }

        let skus = '';
        await asyncForEach(linked_products, async (lp: any) => {
            skus += lp.sku + '|' + lp.qty + '|'
        });

        let payload: any = {
            autosync,
            price_sync,
            stock_sync,
            sync_warehouses,
            publication,
            title,
            description,
            attributes,
            cost,
            skus,
            channels,
            category_variations: publication.category_variations,
            linked_products
        };

        await directusApi('POST', 'custom/legacy-mercadolibre/publicationML', payload)
            .then(async (publicationMl: any) => {
                // commit('setOrdersSchedule', publicationMl.data.schedule);
                console.log('ML Post created... Directus Post in progress...', publicationMl);
                console.log(1);
                let url = '';
                let method;
                let msg = '';
                let error = '';

                let payloadp = {
                    autosync,
                    price_sync,
                    stock_sync,
                    title: publicationMl.data.title,
                    listing_type: publicationMl.data.listing_type_id,
                    logistic_type: publicationMl.data.shipping.logistic_type,
                    last_sync_up: moment().format('YYYY-MM-DD H:mm:ss'),
                    last_sync_down: moment().format('YYYY-MM-DD H:mm:ss'),
                    last_update: moment().format('YYYY-MM-DD H:mm:ss'),
                    created_at: moment().format('YYYY-MM-DD H:mm:ss'), // check date when post is created
                    status: publicationMl.data.status,
                    category_id: publicationMl.data.category_id,
                    linked_products,
                    sync_warehouses,
                    ml_store: payload.publication.officialStore == 0 ? 'prodelec' : payload.publication.officialStore,
                    created_by,
                    channels: payload.publication.channels,
                    variations: publicationMl.data.variations
                }

                if(publicationMl.data.id){
                    url = `items/mercadolibre_posts/${publicationMl.data.id}`;
                    method = 'PATCH';
                    msg = 'Directus Post updated';
                    error = 'error updated a Directus Post';
                }else{
                    url = 'items/mercadolibre_posts';
                    method = 'POST';
                    const data = { ...payloadp, id:publicationMl.data.id };
                    payloadp = data;
                    msg ='Directus Post created';
                    error = 'error created a Directus Post';
                }
                
                await directusApi(method, url, payloadp)
                    .then(res => {
                        console.log(res);
                        console.log(url, method);
                        console.log(msg);
                        console.log(2);
                        dispatch('getOne', { id:res.data.data.id });
                        commit('TOGGLE_ACTIVE_PUBLICATION_LOADING', false);
                    }).catch(err => {
                        console.log(3);
                        console.log(error, err);
                    });
               

            }).catch(err => {
                console.log('error creating a ML Post ', err);
            });
    },

    async postVariations({ _, commit, dispatch }: any, { publication, publicationML, linked_products }: any) {
        let payloadVariation = [];
        let payloadVariationuP = [];

        if (publicationML) {
            if (publicationML.data.variations.length > 0) {
                payloadVariation = publicationML.data.variations.map(v => ({
                    id: v.id,
                    post_id: publication.id,
                    name: v.attribute_combinations.reduce((acc, val) => acc + ' ' + val.name + ' ' + val.value_name, '').trim(),
                    linked_products,
                    last_update: publication.last_update,
                    last_sync_down: publication.last_sync_down,
                }));

                payloadVariationuP = payloadVariation.map(({ id, ...rest }) => ({
                    post_id: id,
                    ...rest,
                }));

                for (const [index, item] of payloadVariation.entries()) {
                    const id = item.id;

                    await directusApi('GET', `items/mercadolibre_variations/${id}`
                    ).then(async (resmv: any) => {
                        const variation = resmv.data.data;

                        if (variation) {
                            await directusApi('PATCH', `items/mercadolibre_variations/${id}`, { payloadVariationuP }
                            ).then(async (res: any) => {
                                console.log("variation UPDATED... Directus Post in progress...", res);
                            }).catch(err => {
                                console.log(err);
                            })
                        } else {
                            await directusApi('POST', `items/mercadolibre_variations`, { payloadVariation }
                            ).then(async (res: any) => {
                                console.log("variation created... Directus Post in progress...", res);
                            }).catch(err => {
                                console.log(err);
                            })
                        }
                    }).catch(err => {
                        console.log(err);
                    })
                }
            }
        }
    },

    async syncPost({ _, commit, dispatch}: any, { post }: any) {
        commit('TOGGLE_ACTIVE_PUBLICATION_LOADING', true);

        await directusApi('POST', `custom/legacy-mercadolibre/syncPost`, post)
        .then(res => {
            console.log('response', res);
            commit('SET_ACTIVE_PUBLICATION', res.data);
        })
        .catch(err => {
            console.log(err);
        })
        .finally(() => {
            commit('TOGGLE_ACTIVE_PUBLICATION_LOADING', false);
        });
    },


    async getOne({ _, commit }: any, { id }: any) {
        commit('TOGGLE_ACTIVE_PUBLICATION_LOADING', true);
        if (id != 0) {
            await directusApi('GET', `custom/legacy-mercadolibre/publications/${id}`)
                .then(res => {
                    // console.log('SET_ACTIVE_PUBLICATION', res.data);
                    commit('SET_ACTIVE_PUBLICATION', res.data);
                })
                .catch(err => {
                    console.log(err);
                })
                .finally(() => {
                    commit('TOGGLE_ACTIVE_PUBLICATION_LOADING', false);
                });
        } else {
            this.defaultPublication = this.defaultPublication;
            // console.log('SET_ACTIVE_PUBLICATION', defaultPublication);
            commit('SET_ACTIVE_PUBLICATION', defaultPublication);
            commit('TOGGLE_ACTIVE_PUBLICATION_LOADING', false);
        }
    },
    async getAll({ _, commit }: any, { search, page, sortName, sortValue, filterStatus, search_sku }: any) {
        commit('TOGGLE_ALL_PUBLICATIONS_LOADING', true);
        let limit = 8;
        let params = {};
        if (search) params = { ...params, search };
        if (page) params = { ...params, limit, offset: (page - 1) * limit };
        if (sortName) params = { ...params, sortName };
        if (sortValue) params = { ...params, sortValue };
        if (filterStatus) params = { ...params, search: filterStatus };

        let query = new URLSearchParams(params);

        let endpoint;

        if (search_sku == true) {
            endpoint = `custom/legacy-mercadolibre/linkedProducts?${query.toString()}`
        }
        else{
            endpoint = `items/mercadolibre_posts?${query.toString()}&meta=total_count&fields=*,variations.*`;}
            
        await directusApi('GET', endpoint)
            .then(res => {
                commit('SET_ALL_PUBLICATIONS', {
                    items: res.data.data,
                    perPage: limit,
                    currentPage: page,
                    totalItems: res.data.meta.total_count,
                });
            })
            .catch(err => {
                console.log(err);
            })
            .finally(() => {
                commit('TOGGLE_ALL_PUBLICATIONS_LOADING', false);
            });
    },
    async categoryPredictor({ _, commit }: any, { title }: any) {
        commit('TOGGLE_ACTIVE_PUBLICATION_LOADING', true);
        await directusApi('POST', `custom/legacy-mercadolibre/categoryPredictor`, { title: title })
            .then(async res => {
                let categories = [];
                await asyncForEach(res.data, async (category: any) => {
                    await directusApi('GET', `custom/legacy-mercadolibre/categoryDetail/${category.category_id}`)
                        .then(async categoryDetail => {
                            let path = '';
                            await asyncForEach(categoryDetail.data.path_from_root, async (pathRoot: any) => {
                                path += ' > ' + pathRoot.name
                            });
                            categories.push({
                                name: category.category_name,
                                id: category.category_id,
                                path
                            })
                        }).catch(err => {
                            console.log(err);
                        });
                });
                // console.log({ categories });
                commit('SET_CATEGORY_PREDICTOR', categories);
            })
            .catch(err => {
                console.log(err);
            }).finally(() => {
                commit('TOGGLE_ACTIVE_PUBLICATION_LOADING', false);
            });
    },
    async getCategoryAttributes({ _, commit }: any, { category_id }: any) {
        // console.log({ category_id });
        commit('TOGGLE_ACTIVE_PUBLICATION_LOADING', true);
        if (category_id) {
            await directusApi('GET', `custom/legacy-mercadolibre/categoryAttributes/${category_id}`)
                .then(async res => {
                    let requiredAtt = [];
                    let requiredVari = [];
                    // console.log(res.data);
                    await asyncForEach(res.data, async (attribute: any) => {
                        // console.log(attribute.tags.hasOwnProperty('required'));
                        if (attribute.tags.hasOwnProperty('required')) { //check required property exist
                            if (attribute.tags.required == true) {  // check true
                                requiredAtt.push(attribute);
                            }
                        }
                        if (attribute.tags.hasOwnProperty('catalog_required')) { //check required property exist
                            if (attribute.tags.catalog_required == true) {  // check true
                                requiredAtt.push(attribute);
                            }
                        }
                        if (attribute.tags.hasOwnProperty('allow_variations')) { //check allow_variations property exist
                            if (attribute.tags.allow_variations == true) {  // check true
                                requiredVari.push(attribute);
                            }

                        }
                    });

                    requiredAtt = lodash.uniqBy(requiredAtt, 'id');
                    requiredVari = lodash.uniqBy(requiredVari, 'id');

                    console.log(requiredAtt);
                    console.log(requiredVari);

                    commit('SET_CATEGORY_ATTRIBUTES', requiredAtt);
                    commit('SET_VARIATIONS', requiredVari);
                })
                .catch(err => {
                    console.log(err);
                }).finally(() => {
                    commit('TOGGLE_ACTIVE_PUBLICATION_LOADING', false);
                });
        } else {
            commit('SET_CATEGORY_ATTRIBUTES', []);
            commit('TOGGLE_ACTIVE_PUBLICATION_LOADING', false);
        }
    },
    async getCategoryCommission({ _, commit }: any, { category_id, price, listing_type }: any) {
        // console.log(category_id, ' -- ', price, ' -- ', listing_type);
        await directusApi('POST', `custom/legacy-mercadolibre/commissionCost`, { category_id, price, listing_type })
            .then(async res => {
                let commission = res.data;
                commit('SET_CATEGORY_COMMISSION', commission);
                // console.log(res.data.sale_fee_amount);
                // console.log({ commission });
            })
            .catch(err => {
                console.log(err);
            });
    },
    async updatePremium({ _, commit, dispatch }: any, post: any) {
        if (post.ml_store == 'prodelec' || post.ml_store == 'PRODELEC') {
            await directusApi('POST', `custom/legacy-mercadolibre/prodelec/updatePremium/${post.id}`).then(async res => {
                dispatch('getOne', { id: post.id });
                console.log('post updated :D');
            });
        } else {
            await directusApi('POST', `custom/legacy-mercadolibre/updatePremium/${post.id}`).then(async res => {
                dispatch('getOne', { id: post.id });
                console.log('post updated :D');
            });
        }
    },
    async linkedProductsOff({ _, commit }: any, data: any) {
        commit('linkedSummaryLoading', true);

        let params = '';
        // params += `&filter[order_date][_contains]=${data.date}`;
        // params += `&meta=filter_count&limit=-1`;
        params += `&meta=total_count&fields=*,variations.*&meta=filter_count&limit=-1`;
        let endpoint = `items/mercadolibre_posts?${params}`;
        await directusApi('GET', endpoint)
            .then(async res => {
                let post_without_products = [];
                await asyncForEach(res.data.data, async (post: any) => {
                    if (post.variations.length > 0) {
                        await asyncForEach(post.variations, async (variation: any) => {
                            variation.linked_products.length > 0 ? 1 + 1 : post_without_products.push(post);
                        });
                    } else {
                        post.linked_products.length > 0 ? 1 + 1 : post_without_products.push(post);
                    }

                    if (!post.autosync) {
                        post_without_products.push(post);
                    }
                });

                if (post_without_products.length == 0) {
                    data.vm.$bvToast.toast('Wow al parecer todas las publicaciones están sincronizadas 🥳🥳', {
                        title: `Genial 😎`,
                        variant: 'success',
                        toaster: 'b-toaster-top-center',
                        autoHideDelay: 5000,
                    });
                } else {// Excel in progress
                    let html = `<tr>
                    <td>No. de publicación</td>
                    <td>Título</td>
                    <td>Tienda</td>
                    <td>Estatus</td>
                    <td>Ultima vez sync.</td>
                    <td>Autosync</td>
                    </tr>`;
                    await asyncForEach(post_without_products, async (post: any) => {
                        const storeLabel = publicationStoreBadge(post)?.label || 'Meli 1';
                        html += `<tr>
                        <td>'${post.id}</td>
                        <td>${post.title}</td>
                        <td>${storeLabel}</td>
                        <td>${post.status}</td>
                        <td>${post.last_sync_up == null ? 'Nunca' : post.last_sync_up}</td>
                        <td>${post.autosync == false ? 'Desactivado' : 'Activado'}</td>
                        </tr>`;
                    });
                    const wb: XLSX.WorkBook = XLSX.read(`<table>${html}</table>`, {
                        type: 'string',
                    });
                    XLSX.writeFile(wb, `PublicacionesSinSincronizar.xls`);
                }
            })
            .catch(err => {
                console.log(err);
            });
        commit('linkedSummaryLoading', false);
    },
    async reportByStock({ _, commit }: any, data: any) {
        commit('byStockLoading', true);
        let stock_limit = data.stock;
        let params = '';
        // params += `&filter[autosync][_eq]=true&meta=total_count&meta=filter_count&limit=10`;
        params += `&filter[autosync][_eq]=true&meta=total_count&meta=filter_count&limit=-1`;
        let endpoint = `items/mercadolibre_posts?${params}`;
        await directusApi('GET', endpoint)
            .then(async res => {
                let posts = res.data.data;
                posts = lodash.filter(posts, function (p: any) {
                    if (p.stock_now <= stock_limit && p.stock_later <= stock_limit) {
                        return p;
                    }
                });
                if (posts.length == 0) {
                    data.vm.$bvToast.toast('Lo sentimos, no se encuentran registros con los parámetros seleccionados.', {
                        title: `Sin registros`,
                        variant: 'warning',
                        toaster: 'b-toaster-top-center',
                        autoHideDelay: 5000,
                    });
                } else {
                    console.log(posts.length);

                    await asyncForEach(posts, async (post: any) => {

                        if (post.linked_products.length) {
                            post.linked_products = post.linked_products.map((product: any) => product.sku + '|' + product.qty + '|');
                        }
                        if (post.sync_warehouses.length) {
                            post.sync_warehouses = post.sync_warehouses.map((w: any) => w.warehouse);
                        }
                    });
                    // console.log({ posts });

                    // return false;
                    // ***************** START Generating excel
                    let html = `<tr>
                    <td>No. de publicación</td>
                    <td>Título</td>
                    <td>Estatus</td>
                    <td>Sync Precio</td>
                    <td>Sync Stock</td>
                    <td>Stock inmediato</td>
                    <td>Stock para después</td>
                    <td>Skus</td>
                    <td>Almacenes</td>
                    </tr>`;
                    await asyncForEach(posts, async (post: any) => {
                        html += `<tr>
                        <td>${post.id}</td>
                        <td>${post.title}</td>
                        <td>${post.status}</td>
                        <td>${post.price_sync == true ? 'Activado' : 'Desactivado'}</td>
                        <td>${post.stock_sync == true ? 'Activado' : 'Desactivado'}</td>
                        <td>${post.stock_now}</td>
                        <td>${post.stock_later}</td>
                        <td>${post.linked_products}</td>
                        <td>${post.sync_warehouses}</td>
                        </tr>`;
                    });
                    const wb: XLSX.WorkBook = XLSX.read(`<table>${html}</table>`, {
                        type: 'string',
                    });
                    XLSX.writeFile(wb, `stocksReport${stock_limit}.xls`);
                    // ***************** END Generating excel
                }
            })
            .catch(err => {
                console.log(err);
            }).finally(() => {
                commit('byStockLoading', false);
            });
    },

    async reportPosts({ _, commit }: any, { start, end, ml_store, data }) {
        commit('postLoading', true);
        
        const startDate = moment(start).utc().startOf('day');
        const endDate = moment(end).utc().startOf('day');

        const maxEndDate = startDate.clone().add(1, 'year');
        const finalEndDate = endDate.isAfter(maxEndDate) ? maxEndDate : endDate;

        const formattedStart = startDate.format('YYYY-MM-DD[T]HH:mm:ss.SSS[-00:00]');
        const formattedEnd = finalEndDate.format('YYYY-MM-DD[T]HH:mm:ss.SSS[-00:00]');
      
        let endpoint2 = `custom/legacy-mercadolibre/reportPublications`;
        let info = {start: formattedStart, end: formattedEnd, ml_store };              
        console.log(info);
        await directusApi('POST', endpoint2, info)
            .then(async res => {

                let html = `<tr>
                <td>No. de publicación</td>
                <td>Título de la publicación</td>
                <td>No. de Ventas</td>
                <td>No. de Visitas</td>
                </tr>`;
                await asyncForEach(res.data, async (post: any) => {
                    html += `<tr>
                    <td>${post.id}</td>
                    <td>${post.title}</td>
                    <td>${post.sales}</td>
                    <td>${post.visits}</td>
                    </tr>`;
                });
                const wb: XLSX.WorkBook = XLSX.read(`<table>${html}</table>`, {
                    type: 'string',
                });

                const startDate = moment(start).format('YYYY-MM-DD');
                const endDate = moment(end).format('YYYY-MM-DD');
                console.log(startDate, endDate);
                
                XLSX.writeFile(wb, `${startDate}-${endDate}postsReport.xls`);
            })
            .catch(err => {
                console.log(err);
            }).finally(() => {
                commit('postLoading', false);
            });
    }
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
